import React, { useState } from 'react';
import { KatDateRangePicker, KatDropdown, KatLabel } from '@amzn/katal-react';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import { DateRange } from './types';
import { formatLocalDate, normalizeToLocalDateString } from './dateUtils';
import style from './DashboardFilters.module.scss';

// Maximum entries the backend accepts for i18nFamilies and packageKeys.
// Coral validation rejects more than this with InvalidInputException.
export const MAX_FILTER_SELECTIONS = 5;

type Props = {
  dateRange: DateRange;
  selectedFamilies: string[];
  selectedPackageKeys: string[];
  availableFamilies: string[];
  availablePackages: string[];
  familiesLoading: boolean;
  packagesLoading: boolean;
  onDateRangeChange: (range: DateRange) => void;
  onFamiliesChange: (families: string[]) => void;
  onPackageKeysChange: (keys: string[]) => void;
};

// Returns true if the YYYY-MM-DD `dateStr` is today or earlier, evaluated
// in the user's local timezone. We compare YYYY-MM-DD strings directly
// (lexicographic order matches calendar order for that format) to avoid the
// UTC/local mismatch that `new Date('YYYY-MM-DD')` introduces — that form is
// parsed as UTC midnight, which is "in the future" for any user east of UTC.
export const isDateNotInFuture = (dateStr: string): boolean => {
  const today = formatLocalDate(new Date());
  return dateStr <= today;
};

// Returns `{ valid: true }` when the range has start <= end, else
// `{ valid: false, errorMessage }`. Both inputs are `YYYY-MM-DD` strings
// (the wire format the dashboard always works in). String comparison is
// safe because YYYY-MM-DD lexicographic order matches calendar order.
export const isValidDateRange = (
  range: { startDate: string; endDate: string } | null,
  errorMessage: string
): { valid: boolean; errorMessage?: string } => {
  if (range && range.startDate > range.endDate) {
    return { valid: false, errorMessage };
  }
  return { valid: true };
};

type DropdownOption = {
  name: string;
  value: string;
  disabled?: boolean;
};

const buildOptions = (
  available: string[],
  selected: string[]
): DropdownOption[] =>
  available.map((v) => ({
    name: v,
    value: v,
    disabled: selected.length >= MAX_FILTER_SELECTIONS && !selected.includes(v),
  }));

const DashboardFilters: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const [dateError, setDateError] = useState<string | null>(null);

  const familyOptions = buildOptions(
    props.availableFamilies,
    props.selectedFamilies
  );
  const packageOptions = buildOptions(
    props.availablePackages,
    props.selectedPackageKeys
  );

  const handleDateChange = (
    e:
      | KatDateRangePicker.ChangeEvent
      | { detail: { startDate: string; endDate: string } }
  ) => {
    const detail = e.detail as { startDate: string; endDate: string };
    if (!detail.startDate || !detail.endDate) {
      return;
    }
    const startStr = normalizeToLocalDateString(detail.startDate);
    const endStr = normalizeToLocalDateString(detail.endDate);

    if (!isDateNotInFuture(startStr) || !isDateNotInFuture(endStr)) {
      setDateError(t('lui-dashboard-filter-date-future'));
      return;
    }

    const validation = isValidDateRange(
      { startDate: startStr, endDate: endStr },
      t('lui-dashboard-filter-date-invalid')
    );
    if (!validation.valid) {
      setDateError(validation.errorMessage ?? null);
      return;
    }

    setDateError(null);
    props.onDateRangeChange({ startDate: startStr, endDate: endStr });
  };

  const handleFamiliesChange = (e: KatDropdown.ChangeEvent) => {
    const next = (e.detail.values ?? []).slice(0, MAX_FILTER_SELECTIONS);
    props.onFamiliesChange(next);
  };

  const handlePackagesChange = (e: KatDropdown.ChangeEvent) => {
    const next = (e.detail.values ?? []).slice(0, MAX_FILTER_SELECTIONS);
    props.onPackageKeysChange(next);
  };

  return (
    <div className={style.row}>
      <div className={style.dateColumn}>
        <KatDateRangePicker
          startLabel={t('lui-dashboard-filter-date-start')}
          endLabel={t('lui-dashboard-filter-date-end')}
          startValue={props.dateRange.startDate}
          endValue={props.dateRange.endDate}
          onChange={handleDateChange}
        />
        {dateError && <KatLabel state="error" text={dateError} />}
      </div>

      <div className={style.familyColumn}>
        <KatDropdown
          label={t('lui-dashboard-filter-family')}
          multiple={true}
          searchable={true}
          options={familyOptions}
          values={props.selectedFamilies}
          placeholder={t('lui-dashboard-filter-family-placeholder')}
          constraintLabel={t('lui-dashboard-filter-max-hint')}
          disabled={props.familiesLoading}
          onChange={handleFamiliesChange}
        />
      </div>

      <div className={style.packageColumn}>
        <KatDropdown
          label={t('lui-dashboard-filter-packages')}
          multiple={true}
          searchable={true}
          options={packageOptions}
          values={props.selectedPackageKeys}
          placeholder={t('lui-dashboard-filter-packages-placeholder')}
          constraintLabel={t('lui-dashboard-filter-max-hint')}
          disabled={props.packagesLoading}
          onChange={handlePackagesChange}
        />
      </div>
    </div>
  );
};

export default DashboardFilters;
